import { BitmapText, Container, Graphics, Sprite, type Texture } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import { RESPINS } from "../sim/offlineMath";
import { BITMAP_FONTS } from "../views/textStyles";
import { METER_LAYOUT, art } from "./layout";
import { FX_SCOPE } from "./fxScope";
import { MeterFlamesView, type MeterFlamesArt } from "./MeterFlamesView";

interface Slot {
  root: Container;
  on: Sprite;
  off: Sprite;
  digit: BitmapText;
}

/**
 * The respin meter under the grid in the bonus (the delivery's frame over three slots): slot n lights gold with its
 * number while at least n respins are left, purple when not. It drops one as a respin starts — the slot's fill draining
 * right to left like a slider, its number fading with it — and lights back up, popping, when a symbol lands. While LIFE
 * is in play green flames burn round it (`MeterFlamesView`). Hidden in the base game.
 */
export class RespinMeterView extends Container {
  private readonly slots: Slot[] = [];
  private readonly runner: PresentationTaskRunner;
  private readonly flames: MeterFlamesView;
  private count = RESPINS;
  /** The drain running (a new count supersedes it). */
  private drainGeneration = 0;

  public constructor(input: { frame: Texture | null; slotOn: Texture | null; slotOff: Texture | null; runner: PresentationTaskRunner; ambientRunner: PresentationTaskRunner }) {
    super({ label: "RespinMeter" });
    this.runner = input.runner;
    this.flames = new MeterFlamesView({ runner: input.ambientRunner });
    const { slot } = METER_LAYOUT;
    const slots = new Container({ label: "Slots" });
    const digits = new Container({ label: "Digits" });
    for (let i = 0; i < RESPINS; i++) {
      const root = new Container({ label: `Slot${i + 1}` });
      root.position.set(art(METER_LAYOUT.slots[i]!), art(METER_LAYOUT.slotY));
      const off = new Sprite({ texture: input.slotOff ?? undefined, anchor: 0.5, label: "Off" });
      off.setSize(art(slot.width), art(slot.height));
      const on = new Sprite({ texture: input.slotOn ?? undefined, anchor: 0.5, label: "On" });
      on.setSize(art(slot.width), art(slot.height));
      root.addChild(off, on);
      slots.addChild(root);
      // The number over the frame, so a lit slot's pop never hides it under the frame's bars.
      const digit = new BitmapText({ text: String(i + 1), style: { fontFamily: BITMAP_FONTS.badge, fontSize: METER_LAYOUT.fontSize }, label: "Digit" });
      digit.anchor.set(0.5);
      digit.position.copyFrom(root.position);
      digits.addChild(digit);
      this.slots.push({ root, on, off, digit });
    }
    // The flames' glow under the slots, their fire over the frame (open on the slots), their sparks over the numbers.
    this.addChild(this.flames.back, slots);
    if (input.frame) {
      const frame = new Sprite({ texture: input.frame, anchor: 0.5, label: "Frame" });
      frame.setSize(art(METER_LAYOUT.art.width), art(METER_LAYOUT.art.height));
      this.addChild(frame);
    }
    this.addChild(this.flames.front, digits, this.flames.top);
    this.visible = false;
    this.render();
  }

  /** The meter hangs from the frame's bottom edge (it moves with the frame as GROW grows it). */
  public hangFrom(frameBottom: number): void {
    this.position.set(0, frameBottom + METER_LAYOUT.belowFrame);
  }

  public setShown(shown: boolean): void {
    this.visible = shown;
  }

  /** The flames' art, loaded after the game starts. */
  public setFlameArt(flames: MeterFlamesArt): void {
    this.flames.setArt(flames);
  }

  /** LIFE is in play (its refill still to come): the flames burn round the meter. */
  public setLifeFlames(burning: boolean): void {
    this.flames.setActive(burning);
  }

  /** Whether the flames show now (the dev hook's read). */
  public get flamesBurning(): boolean {
    return this.visible && this.flames.burning;
  }

  /**
   * `count` respins left: its slots light; a count going up pops the slots it lights; one going down drains the slots it
   * leaves over `drainSeconds`, without holding the caller (the respin rolls meanwhile).
   */
  public async setCount(count: number, popSeconds: number, drainSeconds = 0): Promise<void> {
    const before = this.count;
    this.count = Math.max(0, Math.min(RESPINS, count));
    this.drainGeneration += 1;
    if (this.count < before && drainSeconds > 0) {
      void this.drain(this.slots.slice(this.count, before), drainSeconds);
      return;
    }
    this.render();
    if (this.count <= before || popSeconds <= 0) return;
    const popping = this.slots.slice(before, this.count);
    await this.runner.tweenProgress(popSeconds, (k) => {
      const s = 1 + 0.35 * Math.sin(Math.PI * k);
      for (const slot of popping) if (!slot.digit.destroyed) slot.digit.scale.set(s, s);
    }, FX_SCOPE, { easing: "linear" });
  }

  /** The count shown. */
  public get shown(): number {
    return this.count;
  }

  /** The slots leaving: each fill's right edge slides to its left over the purple, its number fading, then they go out. */
  private async drain(slots: readonly Slot[], seconds: number): Promise<void> {
    const generation = this.drainGeneration;
    const width = art(METER_LAYOUT.slot.width);
    const height = art(METER_LAYOUT.slot.height);
    const masks = slots.map((slot) => {
      clearDrain(slot);
      const mask = new Graphics();
      mask.label = "Drain";
      slot.root.addChild(mask);
      slot.on.mask = mask;
      slot.on.visible = true;
      slot.off.visible = true;
      return mask;
    });
    await this.runner.tweenProgress(seconds, (k) => {
      if (generation !== this.drainGeneration) return;
      masks.forEach((mask, i) => {
        mask.clear().rect(-width / 2, -height / 2, width * (1 - k), height).fill(0xffffff);
        slots[i]!.digit.alpha = 1 - k;
      });
    }, FX_SCOPE, { easing: "sineInOut" });
    // The meter may have gone meanwhile (the intro's, taken down at the player's tap).
    if (generation === this.drainGeneration && !this.destroyed) this.render();
  }

  private render(): void {
    this.slots.forEach((slot, i) => {
      clearDrain(slot);
      const lit = i < this.count;
      slot.on.visible = lit;
      slot.off.visible = !lit;
      slot.digit.visible = lit;
      slot.digit.alpha = 1;
      slot.digit.scale.set(1, 1);
    });
  }
}

/** A drain superseded or done: its mask goes. */
function clearDrain(slot: Slot): void {
  const mask = slot.on.mask;
  if (!(mask instanceof Graphics)) return;
  slot.on.mask = null;
  mask.destroy();
}
